import { Dispatch, SetStateAction, useEffect } from 'react'

import { useToast } from '@chakra-ui/react'
import { useChainModal, useConnectModal } from '@rainbow-me/rainbowkit'
import { avalanche, avalancheFuji } from 'viem/chains'
import { useAccount, useWaitForTransactionReceipt } from 'wagmi'

import { Button } from '@/common/components/Button'
import { useValueTokens } from '@/contexts/VaultTokensContext'
import { useApproveGGPtoVault } from '@/hooks/approve'

export interface ApproveProps {
  amount: bigint
  balance: bigint
  setApproveStatus: Dispatch<SetStateAction<'error' | 'loading' | 'success' | 'idle'>>
}

const ApproveButton = ({ amount, balance, setApproveStatus }: ApproveProps) => {
  const { isConnected, chain } = useAccount()
  const { openConnectModal } = useConnectModal()
  const { openChainModal } = useChainModal()
  const toast = useToast()
  const { currentToken } = useValueTokens()
  const {
    simulation,
    isPending: isApproveLoading,
    writeContract: approve,
    hash,
  } = useApproveGGPtoVault(amount)

  const {
    isLoading,
    isSuccess,
    data: receipt,
    isError,
  } = useWaitForTransactionReceipt({
    hash,
  })

  useEffect(() => {
    if (isSuccess && receipt) {
      if (receipt.success === 1) {
        setApproveStatus('success')
        toast({
          position: 'top',
          title: 'Amount Approved',
          status: 'info',
          duration: 8000,
          description: <span>Please proceed to deposit.</span>,
        })
      } else if (receipt.status === 0) {
        setApproveStatus('error')
      }
    } else if (isError) {
      setApproveStatus('error')
    }
  }, [isSuccess, receipt, isError, toast, setApproveStatus])

  if (!chain) {
    return <Button onClick={openChainModal}>Wrong network</Button>
  }

  if (chain.id != avalanche.id && chain.id != avalancheFuji.id) {
    return <Button onClick={openChainModal}>Wrong network</Button>
  }

  if (amount > balance) {
    return (
      <div className="flex w-full gap-4">
        <Button disabled full variant="destructive-outline">
          Insufficient balance
        </Button>
      </div>
    )
  }

  return isConnected ? (
    <Button
      _loading={{
        bg: isLoading || isApproveLoading ? 'red' : 'inherit',
      }}
      backgroundColor={currentToken.buttonBackgroundColor}
      disabled={!amount || amount <= 0 || isApproveLoading || !approve || isLoading}
      height="14"
      onClick={() => approve(simulation.data.request)}
      size="xl"
      textColor={currentToken.buttonTextColor}
    >
      Approve...
    </Button>
  ) : (
    <Button data-testid="connect" onClick={openConnectModal} size="sm">
      Connect Wallet
    </Button>
  )
}

export default ApproveButton
